// src/app/pages/items/components/ItemPriceManager.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, AlertTriangle, Save, Info, Plus, Pencil, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ItemPrice, ItemPriceCreate, ItemPriceUpdate, PriceTypeEnum } from '@/app/types/pricing';
import { listItemPrices, createItemPrice, updateItemPrice } from '@/app/services/pricingService';
import { calculateSalePriceWithRules } from '@/app/lib/salePriceRuleUtils';
import { Decimal } from 'decimal.js';
import { ItemSalePriceRule } from '@/app/types/itemSalePriceRule';
import { getActiveRulesForItem, createItemSalePriceRule, updateItemSalePriceRule, deleteItemSalePriceRule } from '@/app/services/itemSalePriceRuleService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { triggerItemSync } from '@/lib/indexeddb/syncUtils';

// Define the structure for the form values
type ItemPriceFormValues = {
    [key in PriceTypeEnum]?: {
        id?: number; // Existing ItemPrice record ID
        price: string; // Use string for input control
        is_tax_inclusive: boolean;
        site_id?: number | null; // Add site if needed later
    }
};

// Zod schema for individual price entries within the form
const priceEntrySchema = z.object({
  id: z.number().optional(),
  price: z.string().refine(val => val === '' || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0), {
    message: "Must be a non-negative number",
  }),
  is_tax_inclusive: z.boolean(),
  site_id: z.number().nullable().optional(),
});

// Main form schema dynamically includes all price types
const formSchema = z.object({
    sale: priceEntrySchema.optional(),
    purchase: priceEntrySchema.optional(),
    mrp: priceEntrySchema.optional(),
    cost: priceEntrySchema.optional(),
});

// Add a rule form schema after the existing form schema (around line 45)
const salePriceRuleSchema = z.object({
  pricing_method: z.enum(['fixed_sale_price', 'profit_percentage', 'fixed_profit']),
  value: z.string().refine(val => !isNaN(parseFloat(val)) && parseFloat(val) >= 0, {
    message: "Must be a non-negative number",
  }),
  min_qty: z.string().refine(val => !isNaN(parseFloat(val)) && parseFloat(val) > 0, {
    message: "Min quantity must be greater than 0",
  }).default("0.001"),
  max_qty: z.string().optional(),
  site_id: z.string().optional(),
  is_active: z.boolean().default(true),
  effective_from: z.string().optional(),
  effective_to: z.string().optional(),
});

type SalePriceRuleFormValues = z.infer<typeof salePriceRuleSchema>;

interface ItemPriceManagerProps {
  itemId: number;
  itemName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ItemPriceManager: React.FC<ItemPriceManagerProps> = ({
  itemId,
  itemName,
  open,
  onOpenChange,
}) => {
  const queryClient = useQueryClient();
  const priceTypes: PriceTypeEnum[] = ['sale', 'purchase', 'mrp', 'cost'];
  const debugMode = false; // DEBUG MODE DISABLED

  const prevItemIdRef = useRef<number | undefined>();
  const [purchasePriceForSuggestion, setPurchasePriceForSuggestion] = useState<Decimal | null>(null);
  const [applicableSaleRules, setApplicableSaleRules] = useState<ItemSalePriceRule[]>([]);
  const [selectedTab, setSelectedTab] = useState<string>("base-prices");
  const [isAddRuleFormOpen, setIsAddRuleFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ItemSalePriceRule | null>(null);
  const [deletingRule, setDeletingRule] = useState<ItemSalePriceRule | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const {
    data: existingPrices,
    isLoading: isLoadingPrices,
    isError,
    error,
  } = useQuery({
    queryKey: ['itemPrices', itemId],
    queryFn: async () => {
      const response = await listItemPrices({ item_id: itemId });
      const priceMap: Record<string, ItemPrice> = {};
      response.items.forEach(p => {
        if (p.price_type) {
          if (!priceMap[p.price_type] || p.site_id === null || p.site_id === undefined) {
            priceMap[p.price_type] = p;
          }
        }
      });
      if (debugMode) console.log('[DEBUG] Price map from API:', priceMap);
      return priceMap;
    },
    enabled: open && !!itemId,
    staleTime: 1000 * 60,
  });

  // Query for active sale price rules for this item
  const { 
    data: saleRulesData,
    isLoading: isLoadingSaleRules,
    refetch: refetchSaleRules
  } = useQuery({
    queryKey: ['itemSaleRulesForItem', itemId],
    queryFn: async () => {
      if (!itemId) return [];
      const rules = await getActiveRulesForItem(itemId);
      console.log("Sale price rules fetched:", rules);
      return rules;
    },
    enabled: open && !!itemId
  });

  // Update applicableSaleRules when saleRulesData changes
  useEffect(() => {
    if (saleRulesData) {
      console.log("Setting applicableSaleRules from useEffect:", saleRulesData);
      setApplicableSaleRules(saleRulesData);
    }
  }, [saleRulesData]);

  const form = useForm<ItemPriceFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
  });

  const ruleForm = useForm<SalePriceRuleFormValues>({
    resolver: zodResolver(salePriceRuleSchema),
    defaultValues: {
      pricing_method: 'profit_percentage',
      value: '20', // Default 20% profit
      min_qty: '0.001',
      is_active: true
    },
  });

  useEffect(() => {
    if (debugMode) {
      console.log(
        `[DEBUG] useEffect - Open: ${open}, ItemID: ${itemId}, PrevItemID: ${prevItemIdRef.current}, Dirty: ${form.formState.isDirty}, Touched: ${JSON.stringify(form.formState.touchedFields)}, ExistingPrices: ${existingPrices ? 'loaded' : 'not loaded'}`
      );
    }

    if (open && existingPrices && itemId !== prevItemIdRef.current) {
      if (debugMode) console.log(`[DEBUG] Resetting form. Current ItemID: ${itemId}, PrevItemID: ${prevItemIdRef.current}.`);
      
      const defaultVals: ItemPriceFormValues = {};
      let fetchedPurchasePrice: Decimal | null = null;

      if (existingPrices['purchase']?.price) {
        fetchedPurchasePrice = new Decimal(String(existingPrices['purchase'].price));
        setPurchasePriceForSuggestion(fetchedPurchasePrice);
      }

      // Asynchronously get suggested sale price
      const updateFormWithSuggestedSale = async () => {
        if (!fetchedPurchasePrice || fetchedPurchasePrice.isZero()) {
          console.log("No purchase price available for reference");
        }
        
        priceTypes.forEach(type => {
          const priceData = existingPrices[type];
          let formattedPrice = '';
          if (priceData?.price !== undefined && priceData?.price !== null) {
            const priceNum = parseFloat(String(priceData.price));
            if (!isNaN(priceNum)) {
              formattedPrice = priceNum.toFixed(2);
            }
          }
          defaultVals[type] = {
            id: priceData?.id,
            price: formattedPrice,
            is_tax_inclusive: priceData?.is_tax_inclusive ?? false,
            site_id: priceData?.site_id ?? null,
          };
        });
        
        if (debugMode) console.log('[DEBUG] Setting form defaults:', defaultVals);
        form.reset(defaultVals);
        prevItemIdRef.current = itemId;
      };

      updateFormWithSuggestedSale();
    } else if (open && existingPrices) {
      if (debugMode) console.log(`[DEBUG] Skipping form reset. ItemID: ${itemId} (same as prev), Form dirty: ${form.formState.isDirty}`);
    }
  }, [existingPrices, open, itemId, priceTypes, form, debugMode]);

  const mutation = useMutation({
    mutationFn: async (data: ItemPriceFormValues) => {
      if (debugMode) console.log("[DEBUG] Processing form data for submission:", data);
      const promises: Promise<ItemPrice>[] = [];
      for (const type of priceTypes) {
        const priceData = data[type];
        const existing = existingPrices?.[type];
        if (!priceData) continue;
        const formPriceStr = priceData.price?.trim() ?? '';
        const payloadBase = { item_id: itemId, price_type: type, is_tax_inclusive: priceData.is_tax_inclusive, site_id: priceData.site_id ?? null };
        if (existing?.id) {
          const originalPriceStr = existing.price !== undefined && existing.price !== null ? String(existing.price) : '';
          const taxFlagChanged = priceData.is_tax_inclusive !== existing.is_tax_inclusive;
          if (formPriceStr !== originalPriceStr || taxFlagChanged) {
            let priceValue: number | null = null;
            if (formPriceStr !== '') {
              const parsed = parseFloat(formPriceStr);
              if (!isNaN(parsed) && parsed >= 0) { priceValue = parsed; }
              else { toast.warning(`Invalid price for ${type}. Not saved.`); continue; }
            }
            const updatePayload: ItemPriceUpdate = { ...payloadBase, price: priceValue };
            if (debugMode) console.log(`[DEBUG] Sending update for ${type}:`, updatePayload);
            promises.push(updateItemPrice(existing.id, updatePayload));
          }
        } else if (formPriceStr !== '') {
          const parsed = parseFloat(formPriceStr);
          if (!isNaN(parsed) && parsed >= 0) {
            const createPayload: ItemPriceCreate = { ...payloadBase, price: parsed, is_tax_inclusive: priceData.is_tax_inclusive ?? false };
            if (debugMode) console.log(`[DEBUG] Sending create for ${type}:`, createPayload);
            promises.push(createItemPrice(createPayload));
          }
          else { toast.warning(`Invalid price for ${type}. Not saved.`); }
        }
      }
      if (promises.length === 0) { toast.info("No changes to save."); return []; }
      return Promise.all(promises);
    },
    onSuccess: (data) => {
      if (data && data.length > 0) { toast.success(`Base prices for '${itemName}' updated successfully.`); }
      queryClient.invalidateQueries({ queryKey: ['itemPrices', itemId] });
      // Trigger sync after price update
      // triggerItemSync(); // TEMPORARILY DISABLED for bulk data loading
      onOpenChange(false);
    },
    onError: (error) => { toast.error(`Failed to update prices: ${error.message}`); },
  });

  // Reset rule form when opening the add rule form
  useEffect(() => {
    if (isAddRuleFormOpen && !editingRule) {
      ruleForm.reset({
        pricing_method: 'profit_percentage',
        value: '20',
        min_qty: '0.001',
        is_active: true
      });
    }
  }, [isAddRuleFormOpen, editingRule, ruleForm]);

  // Set form values when editing a rule
  useEffect(() => {
    if (editingRule) {
      const rule = editingRule;
      ruleForm.reset({
        pricing_method: rule.pricing_method as PricingMethodLiteral,
        value: rule.value?.toString() || '',
        min_qty: rule.min_qty?.toString() || '0.001',
        max_qty: rule.max_qty?.toString() || '',
        site_id: rule.site_id?.toString() || '',
        is_active: rule.is_active !== false, // Default to true if undefined
        effective_from: rule.effective_from ? new Date(rule.effective_from).toISOString().split('T')[0] : '',
        effective_to: rule.effective_to ? new Date(rule.effective_to).toISOString().split('T')[0] : '',
      });
      setIsAddRuleFormOpen(true);
    }
  }, [editingRule, ruleForm]);

  // Add rule mutations after the existing mutation (around line 225)
  // Mutations for sale price rules
  const createRuleMutation = useMutation({
    mutationFn: async (data: SalePriceRuleFormValues) => {
      // Convert string values to appropriate types for API
      const payload = {
        item_id: itemId,
        pricing_method: data.pricing_method,
        value: parseFloat(data.value),
        min_qty: parseFloat(data.min_qty || '0.001'),
        max_qty: data.max_qty ? parseFloat(data.max_qty) : undefined,
        site_id: data.site_id ? parseInt(data.site_id) : undefined,
        is_active: data.is_active,
        effective_from: data.effective_from || undefined,
        effective_to: data.effective_to || undefined,
      };
      
      return createItemSalePriceRule(payload);
    },
    onSuccess: () => {
      toast.success("Sale price rule created successfully");
      setIsAddRuleFormOpen(false);
      refetchSaleRules();
      // Trigger sync after rule creation
      // triggerItemSync(); // TEMPORARILY DISABLED for bulk data loading
    },
    onError: (error) => {
      toast.error(`Failed to create sale price rule: ${error.message}`);
    }
  });

  const updateRuleMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number, data: SalePriceRuleFormValues }) => {
      // Convert string values to appropriate types for API
      const payload = {
        pricing_method: data.pricing_method,
        value: parseFloat(data.value),
        min_qty: parseFloat(data.min_qty || '0.001'),
        max_qty: data.max_qty ? parseFloat(data.max_qty) : undefined,
        site_id: data.site_id ? parseInt(data.site_id) : undefined,
        is_active: data.is_active,
        effective_from: data.effective_from || undefined,
        effective_to: data.effective_to || undefined,
      };
      
      return updateItemSalePriceRule(id, payload);
    },
    onSuccess: () => {
      toast.success("Sale price rule updated successfully");
      setIsAddRuleFormOpen(false);
      setEditingRule(null);
      refetchSaleRules();
      // Trigger sync after rule update
      // triggerItemSync(); // TEMPORARILY DISABLED for bulk data loading
    },
    onError: (error) => {
      toast.error(`Failed to update sale price rule: ${error.message}`);
    }
  });

  const deleteRuleMutation = useMutation({
    mutationFn: async (id: number) => {
      return deleteItemSalePriceRule(id);
    },
    onSuccess: () => {
      toast.success("Sale price rule deleted successfully");
      setIsDeleteConfirmOpen(false);
      setDeletingRule(null);
      refetchSaleRules();
      // Trigger sync after rule deletion
      // triggerItemSync(); // TEMPORARILY DISABLED for bulk data loading
    },
    onError: (error) => {
      toast.error(`Failed to delete sale price rule: ${error.message}`);
    }
  });

  // Add handler functions before the return (around line 240)
  const handleSaveRule = async () => {
    const valid = await ruleForm.trigger();
    if (!valid) {
      toast.error("Please correct the errors in the form");
      return;
    }

    const data = ruleForm.getValues();
    
    if (editingRule) {
      updateRuleMutation.mutate({ id: editingRule.id, data });
    } else {
      createRuleMutation.mutate(data);
    }
  };

  const handleDeleteRule = () => {
    if (deletingRule) {
      deleteRuleMutation.mutate(deletingRule.id);
    }
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpenState) => {
        if (!newOpenState && form.formState.isDirty) {
          if (confirm("You have unsaved changes. Are you sure you want to close?")) {
            onOpenChange(newOpenState);
            form.reset({}, { keepValues: false, keepDirty: false, keepDefaultValues: false }); 
          }
        } else {
          onOpenChange(newOpenState);
          if (!newOpenState) {
             form.reset({}, { keepValues: false, keepDirty: false, keepDefaultValues: false }); 
          }
        }
        if (newOpenState) {
            prevItemIdRef.current = undefined; 
        }
      }}
    >
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Prices for: {itemName}</DialogTitle>
          <DialogDescription>
            Set base prices and configure sale price rules for automatic price calculation.
          </DialogDescription>
        </DialogHeader>

        <Tabs 
          value={selectedTab} 
          onValueChange={setSelectedTab} 
          className="w-full"
        >
          <TabsList className="w-full">
            <TabsTrigger value="base-prices" className="flex-1">Base Prices</TabsTrigger>
            <TabsTrigger value="sale-rules" className="flex-1">Sale Price Rules</TabsTrigger>
          </TabsList>
          
          {/* Base Prices Tab */}
          <TabsContent value="base-prices">
            {applicableSaleRules.length > 0 && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  This item has active sale price rules that may override the Sale Price set below.
                </AlertDescription>
              </Alert>
            )}

            {isLoadingPrices && (
              <div className="flex justify-center items-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}

            {isError && (
              <Alert variant="destructive" className="my-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Failed to load existing prices: {(error as Error)?.message || 'Unknown error'}
                  </AlertDescription>
              </Alert>
            )}

            {!isLoadingPrices && !isError && (
              <Form {...form}>
                <div className="py-4 space-y-6">
                  <div className="grid grid-cols-1 gap-4">
                    {priceTypes.map((type) => (
                      <FormField
                        key={type}
                        control={form.control}
                        name={`${type}.price`}
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                            <FormLabel className="w-24 text-right">{type.charAt(0).toUpperCase() + type.slice(1)} Price:</FormLabel>
                            <FormControl>
                              <div className="flex-1 flex flex-row items-center space-x-2">
                                <div className="flex-1">
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    {...field}
                                  />
                                </div>
                                <div>
                                  <FormField
                                    control={form.control}
                                    name={`${type}.is_tax_inclusive`}
                                    render={({ field }) => (
                                      <FormItem className="flex flex-row items-center space-x-1 space-y-0">
                                        <FormControl>
                                          <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                          />
                                        </FormControl>
                                        <div className="text-xs">Tax Incl.</div>
                                      </FormItem>
                                    )}
                                  />
                                </div>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                </div>
              </Form>
            )}
          </TabsContent>
          
          {/* Sale Price Rules Tab */}
          <TabsContent value="sale-rules">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-md font-medium">Sale Price Rules</h3>
              <Button 
                size="sm" 
                onClick={() => {
                  setEditingRule(null);
                  setIsAddRuleFormOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Rule
              </Button>
            </div>
            
            {isLoadingSaleRules ? (
              <div className="py-4 flex justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : applicableSaleRules.length > 0 ? (
              <div className="space-y-3">
                {applicableSaleRules.map(rule => (
                  <div key={rule.id} className="border rounded-md p-3 bg-muted/30">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-medium">
                          {rule.pricing_method === 'fixed_sale_price' 
                            ? 'Fixed Sale Price' 
                            : rule.pricing_method === 'profit_percentage'
                              ? 'Profit Percentage'
                              : 'Fixed Profit'}
                        </span>
                        <span className="ml-2 text-sm">
                          {rule.pricing_method === 'fixed_sale_price' 
                            ? `₹${new Decimal(String(rule.value)).toFixed(2)}` 
                            : rule.pricing_method === 'profit_percentage'
                              ? `${new Decimal(String(rule.value)).toFixed(2)}%`
                              : `₹${new Decimal(String(rule.value)).toFixed(2)}`}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setEditingRule(rule)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setDeletingRule(rule);
                            setIsDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="text-xs text-muted-foreground mt-1">
                      <span>Min Qty: {new Decimal(String(rule.min_qty)).toFixed(3)}</span>
                      {rule.max_qty && (
                        <span className="ml-2">Max Qty: {new Decimal(String(rule.max_qty)).toFixed(3)}</span>
                      )}
                      {rule.site_id && (
                        <span className="ml-2">Site: {rule.site_id}</span>
                      )}
                    </div>
                    
                    {(rule.effective_from || rule.effective_to) && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {rule.effective_from && (
                          <span>From: {new Date(rule.effective_from).toLocaleDateString()}</span>
                        )}
                        {rule.effective_to && (
                          <span className="ml-2">To: {new Date(rule.effective_to).toLocaleDateString()}</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-10 text-sm text-muted-foreground text-center border rounded-md">
                No sale price rules defined for this item.
                <p className="mt-2">
                  Click the "Add Rule" button to create your first rule.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Sale Price Rule Form Dialog */}
        <Dialog open={isAddRuleFormOpen} onOpenChange={setIsAddRuleFormOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingRule ? 'Edit' : 'Add'} Sale Price Rule</DialogTitle>
              <DialogDescription>
                Configure how the sale price should be calculated
              </DialogDescription>
            </DialogHeader>
            
            <Form {...ruleForm}>
              <div className="space-y-4 py-2">
                <FormField
                  control={ruleForm.control}
                  name="pricing_method"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pricing Method</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a pricing method" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="profit_percentage">Profit Percentage</SelectItem>
                          <SelectItem value="fixed_profit">Fixed Profit Amount</SelectItem>
                          <SelectItem value="fixed_sale_price">Fixed Sale Price</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={ruleForm.control}
                  name="value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {ruleForm.watch('pricing_method') === 'profit_percentage' 
                          ? 'Profit Percentage (%)' 
                          : ruleForm.watch('pricing_method') === 'fixed_profit'
                            ? 'Fixed Profit Amount'
                            : 'Fixed Sale Price'}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder="0.00"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={ruleForm.control}
                    name="min_qty"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Min Quantity</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="0.001"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={ruleForm.control}
                    name="max_qty"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Quantity</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="Optional"
                            {...field}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={ruleForm.control}
                    name="effective_from"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Effective From</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            {...field}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={ruleForm.control}
                    name="effective_to"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Effective To</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            {...field}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={ruleForm.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Active</FormLabel>
                        <p className="text-sm text-muted-foreground">
                          If checked, this rule will be applied when calculating prices
                        </p>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
              
              <DialogFooter className="mt-4">
                <Button 
                  type="button" 
                  variant="secondary" 
                  onClick={() => {
                    setIsAddRuleFormOpen(false);
                    setEditingRule(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  onClick={handleSaveRule}
                  disabled={createRuleMutation.isPending || updateRuleMutation.isPending}
                >
                  {createRuleMutation.isPending || updateRuleMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>Save Rule</>
                  )}
                </Button>
              </DialogFooter>
            </Form>
          </DialogContent>
        </Dialog>
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Deletion</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this sale price rule? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            
            <DialogFooter className="mt-4">
              <Button 
                type="button" 
                variant="secondary" 
                onClick={() => {
                  setIsDeleteConfirmOpen(false);
                  setDeletingRule(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                variant="destructive"
                onClick={handleDeleteRule}
                disabled={deleteRuleMutation.isPending}
              >
                {deleteRuleMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>Delete</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        <DialogFooter className="pt-4">
          <Button 
            type="button" 
            variant="secondary" 
            onClick={() => {
              if (!form.formState.isDirty || confirm("You have unsaved changes. Are you sure?")) { 
                onOpenChange(false); 
                form.reset({}, { keepValues: false, keepDirty: false, keepDefaultValues: false });
              }
            }} 
            disabled={mutation.isPending}
          >
            Close
          </Button>
          {selectedTab === "base-prices" && (
            <Button 
              type="button" 
              disabled={mutation.isPending || isLoadingPrices} 
              onClick={() => {
                form.trigger().then(isValid => { 
                  if (isValid) { 
                    mutation.mutate(form.getValues()); 
                  } else { 
                    toast.error("Please correct errors.");
                  }
                });
              }}
            >
              {mutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {mutation.isPending ? 'Saving...' : 'Save Prices'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ItemPriceManager; 